/**
 * StaffManagement.tsx — Dedicated team management and client-onboarding entry point
 * Design policy: Readability first — generous whitespace, large type, clear hierarchy.
 * Accessible via Team Access & Add Client (admin and controller roles).
 */

import { useState } from "react";
import { useLocation } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { isAdmin as checkIsAdmin, canAccessTeamManagement } from "@/lib/roleGuard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLE_LABELS: Record<string, string> = {
  super_admin: "Director",
  admin: "Director",
  controller: "Controller",
  bookkeeping_manager: "Bkkpr Mgr",
  bookkeeper_supervisor: "Bkkpr",
  bookkeeper: "Bkkpr",
  tax_division: "Tax Division",
};

// All roles use neutral light grey — no colored badges
const NEUTRAL_ROLE = { bg: "oklch(0.92 0.008 240)", text: "oklch(0.08 0.01 240)", border: "oklch(0.78 0.02 240)" };

function getRoleColor(_role: string) {
  return NEUTRAL_ROLE;
}

const inputCls =
  "w-full px-4 py-3 rounded-xl text-sm bg-[oklch(0.93_0.008_240)] border border-[oklch(1_0_0/10%)] " +
  "text-[oklch(0.08_0.01_240)] placeholder-[oklch(0.4_0.02_240)] focus:outline-none " +
  "focus:border-[oklch(0.72_0.14_75)] transition-colors";

export default function StaffManagement() {
  const { user } = useAuth();
  const [, navigate] = useLocation();

  // Controllers have full management access but cannot see admin-role members
  const isAdmin = checkIsAdmin(user?.role);
  const hasAccess = canAccessTeamManagement(user?.role);

  // All hooks MUST be declared before any conditional returns (React Rules of Hooks)
  const utils = trpc.useUtils();
  const { data: staffList, isLoading } = trpc.staffManagement.list.useQuery(
    { callerEmail: user?.email ?? "" },
    { enabled: !!user?.email && hasAccess }
  );

  const [editingId, setEditingId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState<{ id: number; name: string; active: boolean } | null>(null);
  const [addForm, setAddForm] = useState({
    name: "", email: "", password: "", role: "bookkeeper" as string, initials: "",
  });

  const resetPassword = trpc.staffManagement.resetPassword.useMutation({
    onSuccess: () => {
      toast.success("Password updated successfully.");
      setEditingId(null);
      setNewPassword("");
    },
    onError: (e) => toast.error(e.message),
  });

  const updateRole = trpc.staffManagement.updateRole.useMutation({
    onSuccess: () => {
      toast.success("Role updated.");
      utils.staffManagement.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const setActive = trpc.staffManagement.setActive.useMutation({
    onSuccess: () => {
      toast.success("Staff status updated.");
      utils.staffManagement.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const addStaff = trpc.staffManagement.add.useMutation({
    onSuccess: () => {
      toast.success(`${addForm.name} added to the team.`);
      setShowAddForm(false);
      setAddForm({ name: "", email: "", password: "", role: "bookkeeper", initials: "" });
      utils.staffManagement.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  // Access guard — redirect after all hooks are declared (React Rules of Hooks)
  if (user && !hasAccess) {
    navigate("/dashboard");
    return null;
  }

  // Controllers cannot see admin-role members (security: hide Mr. B's credentials)
  const visibleStaff = isAdmin
    ? staffList
    : staffList?.filter((m) => m.role !== "admin" && m.role !== "super_admin");
  const activeCount = visibleStaff?.filter((m) => m.active).length ?? 0;
  const totalCount = visibleStaff?.length ?? 0;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-8 animate-fade-slide-up">

        {/* ── Page Header ─────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4">
          <div>
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 mb-3">
              <button
                onClick={() => navigate("/settings")}
                className="text-sm text-[oklch(0.35_0.03_240)] hover:text-[oklch(0.08_0.01_240)] transition-colors"
              >
                Settings
              </button>
              <span className="text-[oklch(0.25_0.03_240)] text-sm">›</span>
              <span className="text-sm text-[oklch(0.08_0.01_240)] font-medium">Team Access & Add Client</span>
            </div>
            <h1
              className="text-3xl font-bold text-[oklch(0.12_0.04_240)]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Team Access &amp; Add Client
            </h1>
            <p className="text-sm text-[oklch(0.08_0.01_240)] mt-1.5">
              Add operational clients for Contacts and Schedule &amp; Task Assignment, or manage staff accounts and access.
            </p>
          </div>

          {/* Summary badges */}
          <div className="flex items-center gap-3 shrink-0 mt-1">
            <div
              className="px-4 py-2 rounded-xl text-center"
              style={{ background: "oklch(0.92 0.005 240 / 12%)", border: "1px solid oklch(0.92 0.005 240 / 25%)" }}
            >
              <p className="text-2xl font-bold font-mono" style={{ color: "oklch(0.08 0.01 240)" }}>{activeCount}</p>
              <p className="text-sm uppercase tracking-wider text-[oklch(0.35_0.03_240)]">Active</p>
            </div>
            <div
              className="px-4 py-2 rounded-xl text-center"
              style={{ background: "oklch(0.95 0.006 240)", border: "1px solid oklch(0 0 0 / 10%)" }}
            >
              <p className="text-2xl font-bold font-mono text-[oklch(0.28_0.04_240)]">{totalCount}</p>
              <p className="text-sm uppercase tracking-wider text-[oklch(0.35_0.03_240)]">Total</p>
            </div>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all"
              style={{
                background: showAddForm ? "oklch(0.92 0.005 240 / 25%)" : "oklch(0.82 0.01 240)",
                color: showAddForm ? "oklch(0.08 0.01 240)" : "oklch(0.12 0.02 85)",
                border: showAddForm ? "1px solid oklch(0.92 0.005 240 / 50%)" : "none",
              }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d={showAddForm ? "M6 18L18 6M6 6l12 12" : "M12 4v16m8-8H4"} />
              </svg>
              {showAddForm ? "Cancel" : "Add Staff"}
            </button>
          </div>
        </div>

        <section
          className="rounded-2xl p-6"
          style={{ background: "oklch(0.96 0.025 82)", border: "1px solid oklch(0.78 0.12 75 / 45%)" }}
          aria-label="Operational client onboarding"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[oklch(0.46_0.10_75)]">Operational client onboarding</p>
              <h2 className="mt-1 text-xl font-bold text-[oklch(0.12_0.04_240)]" style={{ fontFamily: "'Playfair Display', serif" }}>Add Client to APX WorkCenter</h2>
              <p className="mt-1 max-w-2xl text-sm text-[oklch(0.30_0.03_240)]">Create a client for Contacts and Schedule &amp; Task Assignment, including bookkeeping tiers, due dates, and bookkeeper assignments. This is not a CRM-only contact.</p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/dashboard?onboard=client")}
              className="flex shrink-0 items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all"
              style={{ background: "oklch(0.72 0.14 75)", color: "oklch(0.12 0.04 240)", border: "1px solid oklch(0.62 0.12 75)" }}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
              Add Client
            </button>
          </div>
        </section>

        {/* ── Add Staff Form ───────────────────────────────────────── */}
        {showAddForm && (
          <div
            className="rounded-2xl p-6"
            style={{
              background: "oklch(0.93 0.008 240)",
              border: "1px solid oklch(0.92 0.005 240 / 25%)",
            }}
          >
            <h2
              className="text-lg font-semibold text-[oklch(0.08_0.01_240)] mb-1"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              New Team Member
            </h2>
            <p className="text-sm text-[oklch(0.35_0.03_240)] mb-5">
              They will be prompted to change their password on first login.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[oklch(0.08_0.01_240)] mb-1.5 font-medium">Full Name</label>
                <input
                  className={inputCls}
                  placeholder="e.g. Maria Lopez"
                  value={addForm.name}
                  onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-[oklch(0.08_0.01_240)] mb-1.5 font-medium">Initials</label>
                <input
                  className={inputCls}
                  placeholder="e.g. ML"
                  maxLength={4}
                  value={addForm.initials}
                  onChange={(e) => setAddForm((f) => ({ ...f, initials: e.target.value.toUpperCase() }))}
                />
              </div>
              <div>
                <label className="block text-sm text-[oklch(0.08_0.01_240)] mb-1.5 font-medium">Email Address</label>
                <input
                  className={inputCls}
                  type="email"
                  placeholder="email@example.com"
                  value={addForm.email}
                  onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value.toLowerCase().trim() }))}
                />
              </div>
              <div>
                <label className="block text-sm text-[oklch(0.08_0.01_240)] mb-1.5 font-medium">Temporary Password</label>
                <input
                  className={inputCls}
                  type="password"
                  placeholder="Min 6 characters"
                  value={addForm.password}
                  onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-[oklch(0.08_0.01_240)] mb-1.5 font-medium">Role</label>
                <select
                  className={inputCls}
                  value={addForm.role}
                  onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value }))}
                >
                  <option value="bookkeeper">Bkkpr</option>
                  <option value="bookkeeping_manager">Bkkpr Mgr</option>
                  <option value="controller">Controller</option>
                  <option value="tax_division">Tax Division</option>
                  <option value="admin">Director</option>
                </select>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                onClick={() =>
                  addStaff.mutate({
                    name: addForm.name,
                    email: addForm.email,
                    password: addForm.password,
                    role: addForm.role as "admin" | "controller" | "bookkeeping_manager" | "bookkeeper_supervisor" | "bookkeeper" | "tax_division",
                    initials: addForm.initials,
                    callerEmail: user?.email ?? "",
                  })
                }
                disabled={addStaff.isPending || !addForm.name || !addForm.email || !addForm.password || !addForm.initials}
                className="px-6 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50"
                style={{ background: "oklch(0.82 0.01 240)", color: "oklch(0.1 0.02 85)" }}
              >
                {addStaff.isPending ? "Adding…" : "Add Team Member"}
              </button>
            </div>
          </div>
        )}

        {/* ── Staff Cards ──────────────────────────────────────────── */}
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-20 rounded-2xl animate-pulse" style={{ background: "oklch(0.93 0.008 240)" }} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {visibleStaff?.map((member) => {
              const rc = getRoleColor(member.role);
              const isEditing = editingId === member.id;
              return (
                <div
                  key={member.id}
                  className="rounded-2xl px-6 py-5 transition-all"
                  style={{
                    background: member.active ? "oklch(0.93 0.008 240)" : "oklch(0.95 0.006 240)",
                    border: "1px solid oklch(1 0 0 / 6%)",
                    opacity: member.active ? 1 : 0.6,
                  }}
                >
                  <div className="flex items-center gap-5">
                    {/* Avatar */}
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold shrink-0"
                      style={{ background: rc.bg, color: rc.text, border: `1px solid ${rc.border}` }}
                    >
                      {member.initials}
                    </div>

                    {/* Name + email */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-0.5">
                        <span className="text-base font-semibold text-[oklch(0.12_0.04_240)]">{member.name}</span>
                        {!member.active && (
                          <span className="text-sm px-2 py-0.5 rounded-full bg-[oklch(0.3_0.03_240)] text-[oklch(0.35_0.03_240)]">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[oklch(0.08_0.01_240)] truncate">{member.email}</p>
                    </div>

                    {/* Role badge + selector — visible to admin and controller, but admin option hidden from controllers */}
                    <div className="shrink-0">
                      <select
                        value={member.role}
                        onChange={(e) =>
                          updateRole.mutateAsync({
                            staffId: member.id,
                            role: e.target.value as "admin" | "controller" | "bookkeeping_manager" | "bookkeeper_supervisor" | "bookkeeper" | "tax_division",
                            callerEmail: user?.email ?? "",
                          })
                        }
                        className="text-sm px-3 py-1.5 rounded-lg font-medium cursor-pointer border-0 outline-none"
                        style={{ background: rc.bg, color: rc.text }}
                      >
                        <option value="bookkeeper">Bkkpr</option>
                        <option value="bookkeeping_manager">Bkkpr Mgr</option>
                        <option value="controller">Controller</option>
                        <option value="tax_division">Tax Division</option>
                        {isAdmin && <option value="admin">Director</option>}
                      </select>
                    </div>

                    {/* Actions — super_admin is protected: no reset or deactivate allowed */}
                    <div className="flex items-center gap-2 shrink-0">
                      {member.role === "super_admin" ? (
                        <span className="px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide" style={{ background: "oklch(0.95 0.04 75)", color: "oklch(0.45 0.12 75)" }}>Protected Account</span>
                      ) : (
                        <>
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="password"
                            placeholder="New password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-36 px-3 py-1.5 rounded-lg text-sm bg-[oklch(0.93_0.008_240)] border border-[oklch(0.82_0.02_240)] text-[oklch(0.08_0.01_240)] outline-none focus:border-[oklch(0.72_0.14_75)]"
                            autoFocus
                          />
                          <button
                            onClick={() => resetPassword.mutate({ staffId: member.id, newPassword, callerEmail: user?.email ?? "" })}
                            disabled={newPassword.length < 6 || resetPassword.isPending}
                            className="px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-40 transition-all"
                            style={{ background: "oklch(0.82 0.01 240)", color: "oklch(0.1 0.02 85)" }}
                          >
                            Save
                          </button>
                          <button
                            onClick={() => { setEditingId(null); setNewPassword(""); }}
                            className="px-3 py-1.5 rounded-lg text-sm transition-all"
                            style={{ background: "oklch(0.94 0.018 220)", color: "oklch(0.14 0.04 240)" }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingId(member.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:bg-[oklch(1_0_0/6%)]"
                          style={{ color: "oklch(0.14 0.04 240)" }}
                          title="Reset password"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                          </svg>
                          Reset Password
                        </button>
                      )}

                      <button
                        onClick={() => setConfirmDeactivate({ id: member.id, name: member.name, active: member.active })}
                        disabled={setActive.isPending}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:bg-[oklch(1_0_0/6%)]"
                        style={{ color: member.active ? "oklch(0.08 0.01 240)" : "oklch(0.08 0.01 240)" }}
                        title={member.active ? "Deactivate account" : "Reactivate account"}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d={member.active
                            ? "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                            : "M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"} />
                        </svg>
                        {member.active ? "Deactivate" : "Reactivate"}
                      </button>
                      </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Footer note ─────────────────────────────────────────── */}
        <p className="text-sm text-[oklch(0.28_0.03_240)] text-center pb-4">
          Team & Access is available to Admin and Controllers. Admin accounts are only visible to Mr. B.
        </p>
      </div>

      {/* ── Deactivate Confirmation Dialog ──────────────────────── */}
      <AlertDialog open={!!confirmDeactivate} onOpenChange={(open) => { if (!open) setConfirmDeactivate(null); }}>
        <AlertDialogContent style={{ background: "oklch(0.93 0.008 240)", border: "1px solid oklch(0 0 0 / 12%)" }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[oklch(0.12_0.04_240)]">
              {confirmDeactivate?.active ? "Deactivate" : "Reactivate"} {confirmDeactivate?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[oklch(0.08_0.01_240)]">
              {confirmDeactivate?.active
                ? `${confirmDeactivate.name} will immediately lose access to the APX Hub. This can be reversed at any time.`
                : `${confirmDeactivate?.name} will regain access to the APX Hub using their existing credentials.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="text-[oklch(0.08_0.01_240)] border-[oklch(1_0_0/12%)] hover:bg-[oklch(1_0_0/5%)] bg-transparent"
              onClick={() => setConfirmDeactivate(null)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="font-semibold"
              style={{
                background: confirmDeactivate?.active ? "oklch(0.55 0.01 240)" : "oklch(0.55 0.01 240)",
                color: "white",
              }}
              onClick={() => {
                if (!confirmDeactivate) return;
                setActive.mutate({ staffId: confirmDeactivate.id, active: !confirmDeactivate.active, callerEmail: user?.email ?? "" });
                setConfirmDeactivate(null);
              }}
            >
              {confirmDeactivate?.active ? "Yes, Deactivate" : "Yes, Reactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
